import { toIsoDate, toSamplePhone } from '@pesto/utils';

const places = [
  { city: 'New York', state: 'NY', zip: '10001' },
  { city: 'Los Angeles', state: 'CA', zip: '90012' },
  { city: 'Chicago', state: 'IL', zip: '60601' },
  { city: 'Houston', state: 'TX', zip: '77002' },
  { city: 'Miami', state: 'FL', zip: '33130' },
  { city: 'San Francisco', state: 'CA', zip: '94103' },
  { city: 'Seattle', state: 'WA', zip: '98101' },
  { city: 'Boston', state: 'MA', zip: '02108' },
  { city: 'Washington', state: 'DC', zip: '20001' },
  { city: 'Atlanta', state: 'GA', zip: '30303' },
];

const streets = ['Main Street', 'Oak Avenue', 'Maple Drive', 'Cedar Lane', 'Park Avenue', 'Elm Street'];

const names = [
  'John',
  'Jane',
  'Jim',
  'Jill',
  'Jack',
  'James',
  'Jennifer',
  'Jessica',
  'Joseph',
  'Julia',
  'Michael',
  'Michelle',
  'Matthew',
  'Mary',
  'Mark',
  'Margaret',
  'Martin',
  'Melissa',
  'Maria',
  'Mason',
  'William',
  'Wendy',
  'Walter',
  'Wanda',
  'Wayne',
  'Willow',
  'Warren',
  'Whitney',
  'Wesley',
  'Willa',
  'David',
  'Diana',
  'Daniel',
  'Donna',
  'Donald',
  'Deborah',
  'Dennis',
  'Dorothy',
  'Douglas',
  'Daisy',
  'Robert',
  'Rachel',
  'Richard',
  'Rebecca',
  'Ronald',
  'Rose',
  'Roger',
  'Ruth',
  'Raymond',
  'Rita',
];

const lastNames = [
  'Doe',
  'Smith',
  'Johnson',
  'Williams',
  'Brown',
  'Jones',
  'Garcia',
  'Miller',
  'Davis',
  'Rodriguez',
  'Wilson',
  'Martinez',
  'Anderson',
  'Taylor',
  'Thomas',
  'Moore',
  'Jackson',
  'Martin',
  'Lee',
  'Thompson',
  'White',
  'Harris',
  'Clark',
  'Lewis',
  'Robinson',
  'Walker',
  'Young',
  'Allen',
  'King',
  'Wright',
  'Scott',
  'Green',
  'Baker',
  'Adams',
  'Nelson',
  'Hill',
  'Campbell',
  'Mitchell',
  'Roberts',
  'Carter',
  'Phillips',
  'Evans',
  'Turner',
  'Torres',
  'Parker',
  'Collins',
  'Edwards',
  'Stewart',
  'Morris',
  'Murphy',
];

const randomFrom = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

const randomBetween = (min: number, max: number) => Math.floor(Math.random() * (max - min) + min);

const padded = (value: number, length: number) => value.toString().padStart(length, '0');

const randomDob = () =>
  `${padded(randomBetween(1, 29), 2)}-${padded(randomBetween(1, 13), 2)}-${new Date().getFullYear() - randomBetween(18, 50)}`;

const randomSsn = () =>
  `${padded(randomBetween(1, 666), 3)}-${padded(randomBetween(1, 100), 2)}-${padded(randomBetween(1, 10000), 4)}`;

const daysAgo = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() - days);

  return toIsoDate(d);
};

const monthsFromNow = (months: number) => {
  const d = new Date();
  d.setMonth(d.getMonth() + months);

  return toIsoDate(d);
};

export type SampleVariant = 'standard' | 'promo' | 'promoOpeningBalance';

const sampleVariantConfig: Record<SampleVariant, { hasPromo: boolean; hasOpeningBalance: boolean }> = {
  standard: { hasPromo: false, hasOpeningBalance: false },
  promo: { hasPromo: true, hasOpeningBalance: false },
  promoOpeningBalance: { hasPromo: true, hasOpeningBalance: true },
};

const generateSampleApplicant = () => {
  const { city, state, zip } = randomFrom(places);

  return {
    email: `sample.user${randomBetween(0, 1000000)}@example.com`,
    first_name: randomFrom(names),
    middle_name: '',
    last_name: randomFrom(lastNames),
    phone: toSamplePhone(randomBetween(0, 1000000000)),
    dob: randomDob(),
    ssn: randomSsn(),
    address_line1: `${randomBetween(1, 10000)} ${randomFrom(streets)}`,
    address_line2: `Apt ${randomBetween(1, 100)}`,
    city,
    state,
    zip,
    card_ref: `CARD-${randomBetween(0, 1000000)}`,
    last_four: padded(randomBetween(0, 10000), 4),
  };
};

const generateSampleLoanTerms = (variant: SampleVariant) => {
  const { hasPromo, hasOpeningBalance } = sampleVariantConfig[variant];

  return {
    credit_limit: randomBetween(500000, 1000000),
    partner_account_number: `PA-${randomBetween(0, 1000000)}`,
    origination_date: daysAgo(3),
    initial_balance: hasOpeningBalance ? randomBetween(100000, 500000) : 0,
    promo_end_date: hasPromo ? monthsFromNow(3) : '',
    apr_margin: '2.5',
    initial_apr: '8.5',
  };
};

export const useRandomUserGenerator = () => {
  return { generateSampleApplicant, generateSampleLoanTerms };
};
